import { useMemo, useState } from "react";
import Swal from "sweetalert2";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { brand } from "../../../config/theme";
import { cn } from "../../../components/ui";
import { useVehicles } from "../hooks/useVehicles";
import { useMxLabels } from "../hooks/useMxLabels";
import { useMaintenanceRecords } from "../hooks/useMaintenanceRecords";
import { generateMaintenanceReport, type MaintenanceReportKind } from "../services/maintenanceService";
import { CHART_AXIS, CHART_COLORS, MAINTENANCE_TYPES, MODE_CHART_COLORS } from "../theme";
import { costByCategory, costByMonth, costByVehicle, costOf, fmtMoney, fmtMonth, lastMonths, monthKey, serviceDateOf } from "../lib";
import { KpiCard, PageShell, Panel, ToolbarButton, TruncationNotice } from "../components/kit/Primitives";
import { VehicleThumb } from "../components/kit/Media";

const TYPE_COLORS: Record<string, string> = { programado: "#3b82f6", solicitado: "#f59e0b", sugerido: "#8b5cf6" };

// Reportes: análisis de costos de los últimos 6 meses (por mes y tipo, propio
// vs. externo, por categoría y por unidad) y el generador de PDF de historial
// y costos (flota o unidad, rango de fechas y envío opcional por correo).
export default function ReportesSection() {
  const L = useMxLabels();
  const { t } = L;
  const { vehicles, vehicleOf } = useVehicles();
  const { rows, total, loading, loadingMore, reload, loadMore } = useMaintenanceRecords();

  const [kind, setKind] = useState<Exclude<MaintenanceReportKind, "workOrder">>("history");
  const [scope, setScope] = useState<"all" | "vehicle">("all");
  const [vehicleId, setVehicleId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastUrl, setLastUrl] = useState<string | null>(null);

  const months = useMemo(() => lastMonths(6), []);
  const a = useMemo(() => {
    const inRange = rows.filter((r) => r.status === "completado" && months.includes(monthKey(serviceDateOf(r)) || ""));
    const total = inRange.reduce((s, r) => s + costOf(r), 0);
    const ext = inRange.filter((r) => r.workshopMode === "externo").reduce((s, r) => s + costOf(r), 0);
    const byType = months.map((m) => {
      const row: Record<string, number | string> = { month: m, label: fmtMonth(m, L.locale) };
      for (const ty of MAINTENANCE_TYPES) row[ty] = inRange.filter((r) => r.type === ty && monthKey(serviceDateOf(r)) === m).reduce((s, r) => s + costOf(r), 0);
      return row;
    });
    return {
      total,
      count: inRange.length,
      avg: inRange.length ? total / inRange.length : 0,
      ext,
      byMonth: costByMonth(inRange, months).map((m) => ({ ...m, label: fmtMonth(m.month, L.locale) })),
      byType,
      modes: [
        { key: "propio", name: t("mantenimiento.ui.charts.own"), value: total - ext },
        { key: "externo", name: t("mantenimiento.ui.charts.external"), value: ext },
      ].filter((x) => x.value > 0),
      byCategory: costByCategory(inRange).map((c) => ({ ...c, name: L.category(c.category) })),
      byVehicle: costByVehicle(inRange).slice(0, 10),
    };
  }, [rows, months, L, t]);

  const generate = async () => {
    if (scope === "vehicle" && !vehicleId) return;
    setBusy(true);
    setLastUrl(null);
    try {
      const { url, emailed } = await generateMaintenanceReport({
        kind,
        vehicleId: scope === "vehicle" ? vehicleId : undefined,
        startDate: startDate ? new Date(startDate).toISOString() : undefined,
        endDate: endDate ? new Date(endDate).toISOString() : undefined,
        email: email.trim() || undefined,
      });
      setLastUrl(url);
      if (emailed) Swal.fire({ title: t("mantenimiento.reportEmailed"), icon: "success", confirmButtonColor: brand.primary });
      else window.open(url, "_blank");
    } catch {
      Swal.fire({ title: t("mantenimiento.reportError"), icon: "error", confirmButtonColor: brand.primary });
    } finally {
      setBusy(false);
    }
  };

  const money = (n: number, compact = false) => fmtMoney(n, L.locale, compact);
  const inputCls = "w-full rounded-lg border border-line bg-app px-3 py-2 text-sm text-content outline-none focus:border-primary-500";
  const seg = (on: boolean) => cn("flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors", on ? "border-primary-500 bg-primary-500 text-on-primary" : "border-line text-content hover:bg-subtle");
  const maxVehicle = a.byVehicle[0]?.cost || 1;

  return (
    <PageShell
      icon="ri-bar-chart-box-line"
      title={t("mantenimiento.menu.reportes")}
      subtitle={`${t("mantenimiento.ui.reportes.analytics")} · ${t("mantenimiento.ui.reportes.period")}`}
      actions={<ToolbarButton icon="ri-refresh-line" label={t("mantenimiento.ui.refresh")} onClick={reload} spinning={loading} />}
    >
      <TruncationNotice shown={rows.length} total={total} onLoadMore={loadMore} loading={loadingMore} />
      <div className="grid grid-cols-1 gap-4 2xl:grid-cols-[1fr_380px]">
        <div className="min-w-0 space-y-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KpiCard icon="ri-money-dollar-circle-line" tone="primary" label={t("mantenimiento.totalCost")} value={money(a.total)} loading={loading} />
            <KpiCard icon="ri-checkbox-circle-line" tone="success" label={t("mantenimiento.ui.kpi.completed")} value={a.count} loading={loading} />
            <KpiCard icon="ri-receipt-line" tone="info" label={t("mantenimiento.ui.kpi.avgTicket")} value={money(a.avg)} loading={loading} />
            <KpiCard icon="ri-store-3-line" tone="warning" label={t("mantenimiento.ui.kpi.external")} value={money(a.ext)} hint={a.total ? `${Math.round((a.ext / a.total) * 100)} %` : undefined} loading={loading} />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <Panel title={t("mantenimiento.ui.charts.byType")} icon="ri-bar-chart-grouped-line" className="xl:col-span-2">
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={a.byType} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_AXIS.grid} vertical={false} />
                    <XAxis dataKey="label" tick={CHART_AXIS.tick} axisLine={false} tickLine={false} />
                    <YAxis tick={CHART_AXIS.tick} axisLine={false} tickLine={false} width={64} tickFormatter={(v) => money(Number(v), true)} />
                    <Tooltip contentStyle={CHART_AXIS.tooltip} formatter={(v: number) => money(Number(v))} cursor={{ fill: "rgb(var(--c-subtle))" }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {MAINTENANCE_TYPES.map((ty, i) => (
                      <Bar isAnimationActive={false} key={ty} dataKey={ty} name={L.type(ty)} stackId="t" fill={TYPE_COLORS[ty]} radius={i === MAINTENANCE_TYPES.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
            <Panel title={t("mantenimiento.ui.charts.ownVsExternal")} icon="ri-pie-chart-line">
              <div className="h-64">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie isAnimationActive={false} data={a.modes} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2} stroke="none">
                      {a.modes.map((m) => <Cell key={m.key} fill={MODE_CHART_COLORS[m.key as "propio" | "externo"]} />)}
                    </Pie>
                    <Tooltip contentStyle={CHART_AXIS.tooltip} formatter={(v: number) => money(Number(v))} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <Panel title={t("mantenimiento.ui.charts.costByMonth")} icon="ri-line-chart-line">
              <div className="h-56">
                <ResponsiveContainer>
                  <LineChart data={a.byMonth} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_AXIS.grid} vertical={false} />
                    <XAxis dataKey="label" tick={CHART_AXIS.tick} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="c" tick={CHART_AXIS.tick} axisLine={false} tickLine={false} width={64} tickFormatter={(v) => money(Number(v), true)} />
                    <YAxis yAxisId="n" orientation="right" tick={CHART_AXIS.tick} axisLine={false} tickLine={false} width={28} allowDecimals={false} />
                    <Tooltip contentStyle={CHART_AXIS.tooltip} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line isAnimationActive={false} yAxisId="c" type="monotone" dataKey="total" name={t("mantenimiento.ui.charts.cost")} stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                    <Line isAnimationActive={false} yAxisId="n" type="monotone" dataKey="count" name={t("mantenimiento.ui.charts.orders")} stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Panel>
            <Panel title={t("mantenimiento.ui.charts.byCategory")} icon="ri-price-tag-3-line">
              <div className="h-56">
                <ResponsiveContainer>
                  <BarChart data={a.byCategory} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_AXIS.grid} horizontal={false} />
                    <XAxis type="number" tick={CHART_AXIS.tick} axisLine={false} tickLine={false} tickFormatter={(v) => money(Number(v), true)} />
                    <YAxis type="category" dataKey="name" tick={CHART_AXIS.tick} axisLine={false} tickLine={false} width={92} />
                    <Tooltip contentStyle={CHART_AXIS.tooltip} formatter={(v: number) => money(Number(v))} cursor={{ fill: "rgb(var(--c-subtle))" }} />
                    <Bar isAnimationActive={false} dataKey="cost" name={t("mantenimiento.ui.charts.cost")} radius={[0, 4, 4, 0]}>
                      {a.byCategory.map((c, i) => <Cell key={c.category} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>

          <Panel title={t("mantenimiento.ui.charts.topVehicles")} icon="ri-truck-line" bodyClassName="p-0">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-line">
                {a.byVehicle.map((x, i) => {
                  const v = vehicleOf(x.vehicleId);
                  return (
                    <tr key={x.vehicleId}>
                      <td className="w-8 px-3 py-2 text-xs font-semibold text-content-muted">{i + 1}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2.5">
                          <VehicleThumb vehicle={v} size="sm" />
                          <span className="truncate font-medium text-content">{v?.label ?? x.vehicleId}</span>
                        </div>
                      </td>
                      <td className="hidden w-1/3 px-3 py-2 md:table-cell">
                        <div className="h-2 overflow-hidden rounded-full bg-subtle">
                          <div className="h-full rounded-full bg-primary-500" style={{ width: `${(x.cost / maxVehicle) * 100}%` }} />
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-content-secondary">{t("mantenimiento.ui.historial.services", { count: x.count })}</td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums text-content">{money(x.cost)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Panel>
        </div>

        {/* Generador de PDF */}
        <Panel title={t("mantenimiento.ui.reportes.generator")} icon="ri-file-pdf-line" className="h-fit 2xl:sticky 2xl:top-0">
          <p className="mb-3 text-xs text-content-secondary">{t("mantenimiento.reportesSubtitle")}</p>
          <label className="mb-1 block text-xs font-semibold text-content-secondary">{t("mantenimiento.reportKind")}</label>
          <div className="mb-4 flex gap-2">
            {(["history", "cost"] as const).map((k) => (
              <button key={k} type="button" onClick={() => setKind(k)} className={seg(kind === k)}>
                {t(k === "history" ? "mantenimiento.reportHistory" : "mantenimiento.reportCost")}
              </button>
            ))}
          </div>
          <label className="mb-1 block text-xs font-semibold text-content-secondary">{t("mantenimiento.reportScope")}</label>
          <div className="mb-3 flex gap-2">
            <button type="button" onClick={() => setScope("all")} className={seg(scope === "all")}>{t("mantenimiento.reportFleet")}</button>
            <button type="button" onClick={() => setScope("vehicle")} className={seg(scope === "vehicle")}>{t("mantenimiento.reportVehicle")}</button>
          </div>
          {scope === "vehicle" ? (
            <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} className={`mb-3 ${inputCls}`}>
              <option value="">{t("mantenimiento.selectVehicle")}</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
            </select>
          ) : null}
          <div className="mb-3 grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-content-secondary">{t("mantenimiento.reportStartDate")}</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-content-secondary">{t("mantenimiento.reportEndDate")}</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputCls} />
            </div>
          </div>
          <label className="mb-1 block text-xs font-semibold text-content-secondary">{t("mantenimiento.reportEmail")}</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("mantenimiento.reportEmailPlaceholder")} className={`mb-4 ${inputCls}`} />
          <button type="button" onClick={generate} disabled={busy || (scope === "vehicle" && !vehicleId)} className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400 disabled:opacity-50">
            <i className={busy ? "ri-loader-4-line animate-spin" : email.trim() ? "ri-mail-send-line" : "ri-download-2-line"} />
            {email.trim() ? t("mantenimiento.sendByEmail") : t("mantenimiento.generatePdf")}
          </button>
          {lastUrl ? (
            <p className="mt-4 text-xs text-content-secondary">
              <i className="ri-checkbox-circle-line mr-1 text-success" />
              {t("mantenimiento.reportReady")}{" "}
              <a href={lastUrl} target="_blank" rel="noreferrer" className="text-primary-500 underline">{t("mantenimiento.openReport")}</a>
            </p>
          ) : null}
        </Panel>
      </div>
    </PageShell>
  );
}
